/**
 * Titres de l'étape finale « inscription sur la liste électorale ».
 *
 * Source UNIQUE : le prédicat était recopié dans Interaction.jsx,
 * BeneficiaireDetail.jsx et BeneficiaryJourneyReport.jsx, et la couche hors ligne
 * en avait besoin à son tour. Quatre copies d'une même règle finissent toujours par
 * diverger (leçon `demandeLabel`, v4.58.1).
 */

/** Format courant : une seule étape porte les deux jalons (dépôt puis retrait). */
export const INSCRIPTION_ETAPE_TITRE = 'Inscription Sur La liste Electorale';

/** Ancien format : deux étapes distinctes. Encore présent sur des parcours anciens. */
export const INSCRIPTION_ETAPES_OLD = ['Inscrit Sur La Liste', 'Carte Retirée'];

/** Id local d'une étape d'inscription créée hors ligne (pas encore sur le serveur). */
export const PREVIEW_INSCRIPTION_ETAPE_ID = 'preview-inscription-0';

/** Une étape créée hors ligne porte un id factice — jamais envoyable tel quel au serveur. */
export function isPreviewEtapeId(id) {
  return String(id || '').startsWith('preview-');
}

export function isInscriptionEtapeTitle(titre) {
  return titre === INSCRIPTION_ETAPE_TITRE || INSCRIPTION_ETAPES_OLD.includes(titre);
}

/**
 * Prédicat LARGE, calqué à l'identique sur `isInscriptionStep` du backend
 * (`parcours.controller.js`). À utiliser **uniquement** pour raisonner sur ce que le
 * serveur compte — typiquement : quels documents bloquent le passage à « Inscrit ».
 *
 * Ne pas confondre avec `isInscriptionEtapeTitle` (titres exacts), qui décide de
 * l'affichage du tableau d'inscription. Les deux sont volontairement distincts ; les
 * unifier changerait l'un ou l'autre comportement.
 */
export function isInscriptionStepLoose(titre) {
  const t = (titre || '').toLowerCase();
  return (
    t.includes('inscription')
    || t.includes('inscri')
    || t.includes('liste electorale')
    || t.includes('carte retirée')
    || t.includes('inscrit sur la liste')
  );
}

/**
 * Documents qui empêchent le passage à « Inscrit », vus **comme le serveur les voit**.
 *
 * Règle PRD § 6.3.3 : « pour compter comme Inscrit : **parcours complet** avec étape
 * inscription obtenue ». Le serveur applique donc, en plus du retrait de carte, la
 * complétude de TOUTES les étapes documentaires du bénéficiaire — sans filtre de CAS,
 * là où l'écran n'affiche que celles du CAS actif. Un document d'un autre CAS resté
 * « non obtenu » bloque donc l'inscription **sans rien afficher**, et le relais voit
 * un parcours « 5/5 » avec un badge « Soumis » qu'aucun message n'explique.
 *
 * @returns {Array<{id: string, titre: string}>} vide si rien ne bloque.
 */
export function documentsBlockingInscrit(etapes) {
  if (!Array.isArray(etapes)) return [];
  return etapes
    .filter((e) => !isInscriptionStepLoose(e?.titre))
    .filter((e) => (e?.document_statut || 'needed') !== 'obtained')
    .map((e) => ({ id: e.id, titre: e.titre }));
}

/** Parcours d'aperçu créé hors ligne : id local, création de phase encore dans l'outbox. */
export function buildOfflinePreviewInscriptionParcours() {
  return {
    etapes: [
      {
        id: PREVIEW_INSCRIPTION_ETAPE_ID,
        titre: INSCRIPTION_ETAPE_TITRE,
        ordre: 1,
        document_statut: 'needed',
        localisation: '',
        statut: 'en_cours',
      },
    ],
  };
}

export function getInscriptionDates(parcours) {
  const allEtapes = parcours?.etapes || [];
  const newFormatEtape = allEtapes.find((e) => e.titre === INSCRIPTION_ETAPE_TITRE);
  const oldFormatEtapes = allEtapes.filter((e) => INSCRIPTION_ETAPES_OLD.includes(e.titre));
  const isNewFormat = !!newFormatEtape;
  const isOldFormat = oldFormatEtapes.length >= 2;
  const etapeInscrit = isOldFormat ? oldFormatEtapes.find((e) => e.titre === 'Inscrit Sur La Liste') : null;
  const etapeCarte = isOldFormat ? oldFormatEtapes.find((e) => e.titre === 'Carte Retirée') : null;
  return {
    inscriptionDate: isNewFormat ? newFormatEtape?.requested_at : etapeInscrit?.requested_at,
    carteRetireeDate: isNewFormat ? newFormatEtape?.obtained_at : etapeCarte?.obtained_at,
  };
}
